/**
 * Oscar intake types.
 *
 * An "intake" is a `pending_approval` project whose `source` is `oscar` — a deal pushed from the
 * Oscar dealdesk pipeline. It carries richer deal-intelligence fields than a plain HubSpot deal
 * (deal amount, opportunity type, requirements, Apollo firmographics, expansion potential, SOW
 * link). All Oscar-specific fields are optional so the UI degrades gracefully when the backend
 * has not populated them yet.
 *
 * Backend contract: GET /api/projects?status=pending_approval&source=oscar
 */
import type { ProjectSummary, ProjectListResponse } from "@/types";

/** Marker for the origin pipeline of a project. */
export type ProjectSource = "oscar" | "hubspot" | "jira" | "manual";

/** A single cross-sell / upsell opportunity surfaced by Oscar. */
export interface OscarUpsellOpportunity {
  domain: string;
  description: string;
}

/**
 * The raw Oscar SNS payload persisted on `projects.oscar_payload` (JSONB). Loosely typed — only
 * the fields the UI reads are declared; everything else is preserved for audit/debugging.
 */
export interface OscarPayload {
  company_overview?: string | null;
  /** Plain-text purpose / business problem / use case (V057 — also promoted to a column). */
  project_summary?: string | null;
  upsale_opportunities?: OscarUpsellOpportunity[] | null;
  [key: string]: unknown;
}

/**
 * A pending_approval project sourced from Oscar, enriched with deal intelligence.
 * Extends {@link ProjectSummary}; every Oscar-only field is optional.
 */
export interface OscarIntake extends ProjectSummary {
  /** Origin pipeline. `oscar` for records surfaced on the Intakes tab. */
  source?: ProjectSource | string | null;

  // ── Deal intelligence (read-only, from Oscar) ──────────────────────────────
  company_name?: string | null;
  company_domain?: string | null;
  deal_name?: string | null;
  /** Raw HubSpot/Oscar deal name column. Alias of {@link deal_name}. */
  dealname?: string | null;
  /** Deal value in USD. */
  deal_amount?: number | null;
  opportunity_type?: string | null;
  technical_requirements?: string | null;
  business_requirements?: string | null;
  aws_customer_segment?: string | null;
  /** Free-text company overview. May also arrive nested under {@link oscar_payload}. */
  company_overview?: string | null;
  /** Upsell/cross-sell opportunities. May also arrive nested under {@link oscar_payload}. */
  upsale_opportunities?: OscarUpsellOpportunity[] | null;
  /** SOW generation progress marker (e.g. "draft", "review", "signed"). */
  sow_gen_progress?: string | null;
  /** SharePoint URL for the architecture diagram, when available. */
  architecture_sharepoint_url?: string;
  /** SharePoint URL for the Essential SMC (Service Management Case) document. */
  essential_smc_sharepoint_url?: string;
  /** SharePoint URL for the Growth SMC (Service Management Case) document. */
  growth_smc_sharepoint_url?: string;
  /** SharePoint URL for the delivery plan document. */
  delivery_plan_sharepoint_url?: string;
  /** SharePoint folder URL containing all Phase 0 documents. */
  sharepoint_folder_url?: string;
  /** ISO timestamp of the last successful Phase 0 document sync to S3. */
  phase0_docs_synced_at?: string;
  /** Status of the Phase 0 document sync to S3. */
  phase0_docs_sync_status?: "pending" | "syncing" | "synced" | "failed";
  /**
   * Apollo-detected tech stack. Backend may return an array or a comma-separated string —
   * normalize with {@link normalizeTechStack} before rendering.
   */
  apollo_tech_stack?: string[] | string | null;
  apollo_employee_count?: number | null;
  expansion_potential?: string | null;
  /** SharePoint URL for the signed/draft SOW document, when available. */
  sow_sharepoint_url?: string | null;
  /** Full raw Oscar SNS payload (audit/debug + nested overview/upsells fallback). */
  oscar_payload?: OscarPayload | null;

  // ── Dealdesk review provenance ─────────────────────────────────────────────
  dealdesk_reviewed_by?: string | null;
  dealdesk_reviewed_at?: string | null;

  /** SA pre-assigned by dealdesk (free text / resource name), when set. */
  assigned_sa?: string | null;
  /** Human-readable name of the SA assigned in Oscar (preferred over the UUID). */
  oscar_assignee_name?: string | null;
  /** Opaque id of the SA assigned in Oscar. */
  oscar_assignee_id?: string | null;

  /** When the intake landed in DeliverPro. Falls back to `approval_requested_at`. */
  intake_received_at?: string | null;

  // ── V057: DEAL_COMMITTED intake fields ─────────────────────────────────────
  /** Project Summary / Business Use Case. Prefilled from Oscar, editable at approval. */
  project_summary?: string | null;
  /** Account Executive — list projection (GET /api/projects). */
  account_executive_name?: string | null;
  account_executive_email?: string | null;
  /**
   * Account Executive — detail projection (GET /api/projects/{id}) returns an object. Typed as a
   * union because the legacy list/detail shapes carried a free-text string here.
   */
  account_executive?: IntakeAccountExecutive | string | null;
  /** HubSpot close date, YYYY-MM-DD (detail only). */
  hubspot_close_date?: string | null;
  /** Presales SA name (detail only). */
  presales_sa_name?: string | null;
}

/** Account Executive as rendered on intake / pending-approval screens. */
export interface IntakeAccountExecutive {
  name: string | null;
  email: string | null;
}

/** List envelope for the intakes query — same shape as projects, typed to {@link OscarIntake}. */
export interface IntakeListResponse extends Omit<
  ProjectListResponse,
  "projects"
> {
  projects: OscarIntake[];
}

/** A single team-member assignment collected by the intake wizard (Step 3). */
export interface IntakeTeamAssignment {
  resource_id: string;
  role_on_project: string;
  /**
   * Always `null` from the UI — capacity allocation is out of MVP scope (Task #10), and the
   * approve endpoint accepts null for every role (nullable since migration V043 (C)).
   *
   * 🚨 Never default this to `100` — it would consume the person's whole cross-project budget.
   */
  capacity_pct: number | null;
}

/**
 * Body for POST /api/projects/{jiraKey}/approve when approving an Oscar intake via the wizard.
 * This is the wizard-facing contract (the exact shape the wizard collects).
 *
 * 🚨 `jira_key` IS DELIBERATELY ABSENT. The backend keys everything off the `{projectId}` path
 *    parameter, and `ApproveProjectSchema` is `.strict()` as of 2026-10-07 — sending `jira_key`
 *    now returns `400 VALIDATION_ERROR` instead of being silently stripped. The wizard renders the
 *    key read-only in Step 2 because renaming it is unsupported (23 FK dependents plus every
 *    provisioned external artefact). Design §2.8.
 */
export interface ApproveIntakePayload {
  title: string;
  project_type: string;
  /**
   * V057: SOW hours, kickoff and expected completion are REQUIRED at approval. The backend checks
   * the effective value (body ?? existing row) and returns 400 VALIDATION_ERROR with
   * `details.missing` when one is absent; the UI always sends all three.
   */
  sow_hours: number;
  planned_kickoff_date: string;
  expected_completion_date: string;
  notes?: string;
  /** V057: Project Summary / Business Use Case (≤ {@link PROJECT_SUMMARY_MAX}). Blank clears it. */
  project_summary?: string;
  team_assignments: IntakeTeamAssignment[];
}

/**
 * The actual request body posted to the approve endpoint. Superset of {@link ApproveIntakePayload}
 * that also carries `solution_architect` / `project_manager` (and optional `engineers_assigned`).
 *
 * As of 2026-10-07 the backend PERSISTS `team_assignments` (into `project_resources`, inside the
 * approval transaction) and the three previously-stripped scalars. The two name fields are still
 * required by the schema, but they are transient: the approval transaction's final step recomputes
 * `projects.{solution_architect,project_manager,engineers_assigned}` from `project_resources` as
 * EMAILS, because three notification paths read those columns as emails. Design §2.3, §2.4.
 */
export interface ApproveIntakeRequestBody extends ApproveIntakePayload {
  solution_architect: string;
  project_manager: string;
  engineers_assigned?: string;
}

/**
 * Body posted by ApprovalModal (Projects page). Since 2026-10-27 it also sends the picked SA / PM /
 * Lead Engineer as `team_assignments` (when the picks carry resource ids), so the approved team
 * are project MEMBERS — the member-only actions (Phase 0, meetings, weekly reports) need that.
 * The backend requires exactly one SA and one PM in the array when it is present.
 */
export interface ApproveProjectRequestBody extends Omit<
  ApproveIntakePayload,
  "team_assignments" | "notes"
> {
  solution_architect: string;
  project_manager: string;
  engineers_assigned?: string;
  team_assignments?: IntakeTeamAssignment[];
}

/** projects.project_summary CHECK + approve-project Zod bound (V057). */
export const PROJECT_SUMMARY_MAX = 20000;

/** The approval inputs that must be set before an approve request is sent (V057). */
export interface RequiredApprovalInputs {
  sowHours: string;
  kickoff: string;
  completion: string;
}

/** Human labels for the required approval fields, in display order. */
export const REQUIRED_APPROVAL_LABELS = {
  sowHours: "SOW Hours",
  kickoff: "Kickoff Date",
  completion: "Expected Completion Date",
} as const;

export type RequiredApprovalField = keyof typeof REQUIRED_APPROVAL_LABELS;

/**
 * Per-field plain-text errors for the required approval inputs (empty object = all valid).
 * SOW hours must be a positive number; the dates must be set and completion must not precede
 * kickoff. Mirrors the backend 400 so the user never round-trips to learn a field is missing.
 */
export function requiredApprovalErrors(
  v: RequiredApprovalInputs,
): Partial<Record<RequiredApprovalField, string>> {
  const errors: Partial<Record<RequiredApprovalField, string>> = {};
  const hours = v.sowHours.trim();
  if (!hours) {
    errors.sowHours = "SOW Hours is required.";
  } else {
    const n = Number(hours);
    if (!Number.isFinite(n) || n <= 0) {
      errors.sowHours = "SOW Hours must be a positive number.";
    }
  }
  if (!v.kickoff) errors.kickoff = "Kickoff Date is required.";
  if (!v.completion) {
    errors.completion = "Expected Completion Date is required.";
  } else if (v.kickoff && v.completion < v.kickoff) {
    errors.completion =
      "Expected Completion Date cannot be before the Kickoff Date.";
  }
  return errors;
}

/** One-line summary of {@link requiredApprovalErrors} for an alert banner, or null when valid. */
export function requiredApprovalSummary(
  errors: Partial<Record<RequiredApprovalField, string>>,
): string | null {
  const messages = (
    Object.keys(REQUIRED_APPROVAL_LABELS) as RequiredApprovalField[]
  )
    .map((f) => errors[f])
    .filter((m): m is string => Boolean(m));
  return messages.length === 0 ? null : messages.join(" ");
}

/**
 * Account Executive for display: the detail object, else the list columns. Null when no name and
 * no email are known — callers then fall back to the HubSpot owner (T-12).
 */
export function intakeAccountExecutive(
  intake: Pick<
    OscarIntake,
    "account_executive" | "account_executive_name" | "account_executive_email"
  >,
): IntakeAccountExecutive | null {
  const ae = intake.account_executive;
  const fromObject = ae && typeof ae === "object" ? ae : null;
  const name =
    fromObject?.name?.trim() ||
    intake.account_executive_name?.trim() ||
    (typeof ae === "string" ? ae.trim() : "") ||
    null;
  const email =
    fromObject?.email?.trim() || intake.account_executive_email?.trim() || null;
  return name || email ? { name, email } : null;
}

/** Project Summary prefill: the column, then the raw Oscar payload, else ''. */
export function intakeProjectSummary(
  intake: Pick<OscarIntake, "project_summary" | "oscar_payload">,
): string {
  const raw =
    intake.project_summary?.trim() ||
    intake.oscar_payload?.project_summary?.trim() ||
    "";
  return raw.slice(0, PROJECT_SUMMARY_MAX);
}

/** Body for POST /api/projects/{id}/reject. */
export interface RejectIntakeInput {
  reason: string;
}

/** Response for POST /api/projects/{id}/phase0/upload — a presigned S3 PUT URL. */
export interface Phase0UploadResponse {
  upload_url: string;
  s3_key: string;
  expires_in: number;
  /**
   * The canonical Content-Type the server signed into the URL (CR §6.1 SEC-4). The PUT **must** send
   * exactly this value — `content-type` is a *signed* header on the presigned URL, so sending the
   * browser's own `File.type` instead will fail with `SignatureDoesNotMatch`. The two legitimately
   * differ: a `.md` file reported by the OS as `text/plain` is accepted but stored as `text/markdown`.
   */
  content_type: string;
}

/** Normalize the Apollo tech stack (array or comma string) into a clean string array. */
export function normalizeTechStack(
  value: OscarIntake["apollo_tech_stack"],
): string[] {
  if (!value) return [];
  const parts = Array.isArray(value) ? value : value.split(",");
  return parts.map((p) => p.trim()).filter((p) => p.length > 0);
}

/** Best display name for the company: company_name → title. */
export function intakeCompany(intake: OscarIntake): string {
  return intake.company_name?.trim() || intake.title;
}

/** Best display name for the deal: deal_name → dealname → title. */
export function intakeDealName(intake: OscarIntake): string {
  return intake.deal_name?.trim() || intake.dealname?.trim() || intake.title;
}

/** SA name to show (never a UUID): oscar_assignee_name → assigned_sa → solution_architect. */
export function intakeAssigneeName(intake: OscarIntake): string | null {
  return (
    intake.oscar_assignee_name?.trim() ||
    intake.assigned_sa?.trim() ||
    intake.solution_architect?.trim() ||
    null
  );
}

/** Company overview, preferring the top-level column then the raw Oscar payload. */
export function intakeCompanyOverview(intake: OscarIntake): string | null {
  return (
    intake.company_overview?.trim() ||
    intake.oscar_payload?.company_overview?.trim() ||
    null
  );
}

/** Upsell opportunities, preferring the top-level column then the raw Oscar payload. */
export function intakeUpsells(intake: OscarIntake): OscarUpsellOpportunity[] {
  const list =
    intake.upsale_opportunities ??
    intake.oscar_payload?.upsale_opportunities ??
    [];
  return Array.isArray(list)
    ? list.filter((u) => u && (u.domain || u.description))
    : [];
}

/** The timestamp an intake arrived, with fallback. */
export function intakeArrivedAt(intake: OscarIntake): string | null {
  return intake.intake_received_at ?? intake.approval_requested_at ?? null;
}

/** Epoch millis for an intake's arrival (0 when unknown) — for sorting. */
export function intakeArrivedMs(intake: OscarIntake): number {
  const ts = intakeArrivedAt(intake);
  const ms = ts ? new Date(ts).getTime() : NaN;
  return Number.isNaN(ms) ? 0 : ms;
}

/** Whole hours elapsed since an ISO timestamp (Infinity when unknown). */
export function hoursSince(iso: string | null | undefined): number {
  if (!iso) return Infinity;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return Infinity;
  return Math.floor((Date.now() - then) / (1000 * 60 * 60));
}

/** True when the intake arrived within the last 48 hours (the "NEW" window). */
export function isNewIntake(intake: OscarIntake): boolean {
  return hoursSince(intakeArrivedAt(intake)) < 48;
}

/** Pluralize a whole-unit count ("1 day" / "18 days"). */
function plural(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? "" : "s"}`;
}

/**
 * Human-readable "x ago" from an ISO timestamp. Units are spelled out ("18 days ago", not "18d
 * ago") — the row is scanned for recency, so the unit must not need decoding.
 */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${plural(mins, "minute")} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${plural(hours, "hour")} ago`;
  const days = Math.floor(hours / 24);
  return `${plural(days, "day")} ago`;
}

/**
 * Absolute received date + time for the tooltip behind {@link timeAgo} — relative time alone gives
 * no way to read the exact arrival moment. Returns null when the timestamp is missing/unparseable.
 */
export function formatExactDateTime(
  iso: string | null | undefined,
): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

/** Format a USD amount compactly (e.g. $120,000). Returns null when unset. */
export function formatCurrency(
  amount: number | null | undefined,
): string | null {
  if (amount == null || Number.isNaN(amount)) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Design-token badge colour classes for an opportunity type:
 * AI/ML = purple, App Dev = blue, App Mod = orange, Migration = yellow, else neutral.
 * Order matters — "App Mod" contains "app", so modernization/migration are matched first.
 *
 * Every hue comes from a named `--color-badge-*` token in globals.css `@theme` — never a stock
 * Tailwind palette class, which is not part of this app's design system.
 *
 * CONTRAST: each category pairs its base hue at /15 (the fill tint) with its darker `-text` step,
 * which is what clears ≥4.5:1 (WCAG 2.1 AA for normal text). Migration was the worst offender and
 * is the reason the text steps are this dark. The token values are pinned to the exact steps those
 * ratios were measured against — see the token block in globals.css before changing one.
 * No `dark:` overrides here — this app registers no `dark` Tailwind variant, so a `dark:` class
 * would resolve off the OS `prefers-color-scheme` and paint a light tone onto the permanently
 * light badge tint (light-on-light, ~1.4:1). See INT-04.
 */
export function opportunityTypeBadgeClass(
  type: string | null | undefined,
): string {
  const t = (type ?? "").toLowerCase();
  if (!t) return "bg-subtle-bg text-text-secondary";
  if (t.includes("ai") || t.includes("ml")) {
    return "bg-badge-aiml/15 text-badge-aiml-text";
  }
  if (t.includes("mod")) {
    return "bg-badge-modernization/15 text-badge-modernization-text";
  }
  if (t.includes("migrat")) {
    return "bg-badge-migration/15 text-badge-migration-text";
  }
  if (t.includes("dev") || t.includes("app")) {
    return "bg-badge-appdev/15 text-badge-appdev-text";
  }
  return "bg-subtle-bg text-text-secondary";
}

/** Project-type option set — re-exported from lib/project-types (single source of truth). */
export { PROJECT_TYPE_OPTIONS } from "@/lib/project-types";

/**
 * Role-on-project option set for the wizard's Step 3 team assignment and the unified Team section.
 *
 * ⚠ THIS IS A MIRROR, NOT THE SOURCE OF TRUTH. The vocabulary is owned by
 * `packages/shared/constants/project-roles.ts` and seeded into `project_role_catalog` by migration
 * V043 (F)/(G).
 *
 * It is duplicated here rather than imported because **the frontend cannot import from
 * `packages/shared`**: `frontend/` is a standalone Vite project with its own `package-lock.json`,
 * it is NOT in the root `workspaces` (`["packages/*", "infra"]`), and it declares no dependency on
 * `@kiro-governance/shared`. `tsconfig.json` maps only `@/*` → `./src/*`. The same constraint is
 * already documented at `src/components/MeetingIntelligenceCard.tsx:21`
 * ("TODO: import from @kiro-governance/shared once workspace alias configured; types duplicated").
 *
 * CR §8.4 specified a re-export from the shared package. That is not achievable without adding
 * `frontend` to the workspace and giving it a build-time dependency on `shared` — a build-system
 * change outside this CR's scope, in a release that is already a coordinated FE+BE deploy.
 *
 * 🚨 THE DUPLICATION IS ENFORCED, NOT TRUSTED. `src/types/__tests__/project-roles-mirror.test.ts`
 * parses the V043 catalog seed and fails if this array drifts from it. Do not edit this list
 * without editing the shared constant AND adding a forward migration — per
 * `aws-architect-standards.md` §0.1, catalog data seeded by an already-applied migration is not
 * re-seedable from code alone.
 *
 * The 4 deprecated legacy roles (`Developer`, `Junior Developer`, `Frontend Developer`,
 * `Backend Developer`) are deliberately ABSENT: they exist in the catalog with `is_active = false`
 * so old records stay readable, but they must never be offered in a picker (OQ-12).
 */
export const PROJECT_ROLE_VALUES = [
  "Leadership",
  "Executive",
  "Solution Architect",
  "Project Manager",
  "Lead Implementation Engineer",
  "Lead Engineer",
  "Engineer",
  "QA Engineer",
  "Designer",
  "Business Analyst",
  "Other",
] as const;

export type ProjectRole = (typeof PROJECT_ROLE_VALUES)[number];

/** Back-compat alias — existing callers import this name. */
export const PROJECT_ROLE_OPTIONS: ReadonlyArray<string> = PROJECT_ROLE_VALUES;

/**
 * DISPLAY labels for role values. Cosmetic only — never a wire value.
 *
 * 🚨 THE KEYS ARE THE BACKEND ENUM; THE VALUES ARE WHAT A HUMAN READS.
 * `PROJECT_ROLE_VALUES` above is the write contract — it is asserted against the V043
 * `project_role_catalog` seed by `project-roles-mirror.test.ts` and must NEVER be edited to change
 * wording. This map exists so a label can be renamed without touching the vocabulary, the Zod
 * enum, the catalog, or any stored `role_on_project` row.
 *
 * Only entries that differ from their key need to appear here; {@link projectRoleLabel} falls
 * through to the raw value otherwise. That fall-through is also what keeps the 4 deprecated legacy
 * roles (`Developer`, `Junior Developer`, `Frontend Developer`, `Backend Developer`) and any future
 * catalog addition rendering correctly without an entry (OQ-12).
 *
 * ⚠️ `Lead Engineer` is intentionally ABSENT and is NOT relabelled. It is a separate legacy enum
 * member with a live row on DP-013 (CR §8.3 BE-2) — distinct from
 * `Lead Implementation Engineer`. Giving both the same display label would make two different
 * stored values indistinguishable in the UI.
 */
export const PROJECT_ROLE_LABELS: Readonly<Record<string, string>> = {
  Executive: "Account Executive",
  "Lead Implementation Engineer": "Implementation Lead",
};

/**
 * How a role value should be shown to a user.
 *
 * Accepts `string` rather than `ProjectRole` on purpose — callers frequently hold a raw DB value
 * which may be one of the 4 deprecated legacy roles. Unrecognised values pass through unchanged,
 * which is the correct and safe default: a role is always readable, never blank.
 */
export function projectRoleLabel(role: string | null | undefined): string {
  if (!role) return "";
  return PROJECT_ROLE_LABELS[role] ?? role;
}

/**
 * Named-lead roles (catalog `is_named_lead`). Mirror of `NAMED_LEAD_ROLES` in
 * `packages/shared/constants/project-roles.ts`.
 *
 * With capacity allocation out of MVP scope (Task #10) the UI uses this only to relax the
 * ResourcePicker's `submits_timesheets` filter for these roles (`requireAssignable={false}`).
 */
export const NAMED_LEAD_ROLES: ReadonlySet<string> = new Set<string>([
  "Leadership",
  "Executive",
  "Solution Architect",
  "Project Manager",
  "Lead Implementation Engineer",
  "Lead Engineer",
]);

/** The DB default from V043 (D). `role_on_project` is NOT NULL, so a write must resolve a value. */
export const DEFAULT_PROJECT_ROLE = "Other";

/** True for a named-lead role. Fails closed (not a named lead) if unrecognised. */
export function isNamedLeadRole(role: string | null | undefined): boolean {
  return !!role && NAMED_LEAD_ROLES.has(role);
}
